import { transformToProcedurePayload } from 'clickNgo/helpers/api'
import { Nullable } from 'cng-common/models/utils'

import { Campaign } from '../../model/campaign'
import { campaignBaseApi } from '../campaignBaseApi'
import {
  buildCampaignCopyParams,
  buildCampaignCreateParams,
  buildCampaignUpdateParams,
  mapCampaignFromRawData,
} from '../mapping'
import {
  CampaignApiTags,
  CopyCampaignParams,
  CreateCampaignParams,
  DeleteCampaignParams,
  GetCampaignsByISRParams,
  GetCampaignsByOwnerParams,
  UpdateCampaignParams,
} from '../models'
import {
  campaignProcedureUrl,
  SP_LUT_CAMPAIGN_COPY,
  SP_LUT_CAMPAIGN_CREATE,
  SP_LUT_CAMPAIGN_DELETE,
  SP_LUT_CAMPAIGN_LIST_GET_BY_ISR,
  SP_LUT_CAMPAIGN_LIST_GET_BY_OWNER,
  SP_LUT_CAMPAIGN_UPDATE,
} from '../procedures'
import { mapFirstRow } from '../responseMappers'

const campaignsEndpoints = campaignBaseApi.injectEndpoints({
  endpoints: (build) => ({
    // Fetch campaigns by ISR
    // This endpoint retrieves campaigns based on the ISR (Inside Sales Representative) identifier.
    // It allows filtering campaigns that are specifically assigned to a given ISR.
    getCampaignsByISR: build.query<{ items: Campaign[]; total: number }, GetCampaignsByISRParams>({
      transformResponse(response) {
        return {
          items: response.Data.map(mapCampaignFromRawData),
          total: response.Total,
        }
      },

      providesTags: () => [{ type: CampaignApiTags.CampaignsByISR, id: 'LIST' }],

      query({ page, pageSize, ...rest }) {
        return {
          url: campaignProcedureUrl(SP_LUT_CAMPAIGN_LIST_GET_BY_ISR),
          method: 'POST',
          data: {
            ...transformToProcedurePayload(rest),
            page,
            pageSize,
          },
        }
      },
    }),

    // Fetch campaigns by Owner
    // This endpoint retrieves campaigns based on the owner identifier.
    getCampaignsByOwner: build.query<
      { items: Campaign[]; total: number },
      GetCampaignsByOwnerParams
    >({
      transformResponse(response) {
        return {
          items: response.Data.map(mapCampaignFromRawData),
          total: response.Total,
        }
      },

      providesTags: () => [{ type: CampaignApiTags.CampaignsByOwner, id: 'LIST' }],

      query({ page, pageSize, ...rest }) {
        return {
          url: campaignProcedureUrl(SP_LUT_CAMPAIGN_LIST_GET_BY_OWNER),
          method: 'POST',
          data: {
            ...transformToProcedurePayload(rest),
            page,
            pageSize,
          },
        }
      },
    }),

    // Create campaign
    // This endpoint creates a new campaign.
    createCampaign: build.mutation<Nullable<Campaign>, CreateCampaignParams>({
      query(params) {
        return {
          url: campaignProcedureUrl(SP_LUT_CAMPAIGN_CREATE),
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          data: buildCampaignCreateParams(params),
        }
      },

      invalidatesTags: () => [
        { type: CampaignApiTags.CampaignsByOwner, id: 'LIST' },
        { type: CampaignApiTags.CampaignsByISR, id: 'LIST' },
      ],

      transformResponse: mapFirstRow(mapCampaignFromRawData),
    }),

    // Copy campaign
    // This endpoint creates a new campaign as a copy of an existing one.
    copyCampaign: build.mutation<Nullable<Campaign>, CopyCampaignParams>({
      query(params) {
        return {
          url: campaignProcedureUrl(SP_LUT_CAMPAIGN_COPY),
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          data: buildCampaignCopyParams(params),
        }
      },

      invalidatesTags: () => [
        { type: CampaignApiTags.CampaignsByOwner, id: 'LIST' },
        { type: CampaignApiTags.CampaignsByISR, id: 'LIST' },
      ],

      transformResponse: mapFirstRow(mapCampaignFromRawData),
    }),

    // Update campaign
    // This endpoint updates the details of a specific campaign.
    updateCampaign: build.mutation<Nullable<Campaign>, UpdateCampaignParams>({
      query({ campaign }) {
        return {
          url: campaignProcedureUrl(SP_LUT_CAMPAIGN_UPDATE),
          method: 'POST',
          data: buildCampaignUpdateParams({ campaign }),
        }
      },

      // Invalidate campaigns lists so queries refetch after update
      invalidatesTags: (_result, _error, params) => [
        { type: CampaignApiTags.CampaignsByOwner, id: 'LIST' },
        { type: CampaignApiTags.CampaignsByISR, id: 'LIST' },
        {
          type: CampaignApiTags.CampaignObjectives,
          id: params.campaign.entryId,
        },
        {
          type: CampaignApiTags.CampaignDocuments,
          id: params.campaign.entryId,
        },
        {
          type: CampaignApiTags.CampaignCallingList,
          id: params.campaign.entryId,
        },
      ],

      transformResponse: mapFirstRow(mapCampaignFromRawData),
    }),

    // Delete campaign
    // This endpoint deletes a specific campaign.
    deleteCampaign: build.mutation<Nullable<Campaign>, DeleteCampaignParams>({
      query({ entryId }) {
        return {
          url: campaignProcedureUrl(SP_LUT_CAMPAIGN_DELETE),
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          data: {
            'p.EntryId': entryId,
          },
        }
      },

      // Invalidate campaigns lists so queries refetch after delete
      invalidatesTags: () => [
        { type: CampaignApiTags.CampaignsByOwner, id: 'LIST' },
        { type: CampaignApiTags.CampaignsByISR, id: 'LIST' },
      ],

      transformResponse: mapFirstRow(mapCampaignFromRawData),
    }),
  }),
})

export const {
  useGetCampaignsByISRQuery,
  useGetCampaignsByOwnerQuery,
  useCreateCampaignMutation,
  useCopyCampaignMutation,
  useUpdateCampaignMutation,
  useDeleteCampaignMutation,
} = campaignsEndpoints
